import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ClipboardCheck, Plus, DoorOpen, DoorClosed, DollarSign, X } from "lucide-react";

const CONDITIONS = ["excellent", "good", "fair", "poor"];

export default function UnitTurnoverPage() {
  const { propertyId } = useProperty();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [selected, setSelected] = useState(null);
  const [dispDialog, setDispDialog] = useState(false);
  const [form, setForm] = useState({
    unit: "", resident_id: "", lease_id: "",
    type: "move_in", overall_condition: "good",
    rooms: [], damages: [], notes: "",
  });
  const [residents, setResidents] = useState([]);
  const [leases, setLeases] = useState([]);
  const [disp, setDisp] = useState({ security_deposit_held: 0, deductions: [{ _uid: crypto.randomUUID(), reason: "", amount: 0 }] });

  const load = async () => {
    if (!propertyId) return;
    try {
      const { data } = await api.get(`/admin/unit-inspections?property_id=${propertyId}`);
      setItems(data.items || []);
      setStats(data.stats || null);
    } catch (e) { toast.error("Failed to load inspections"); }
  };
  const loadPeople = async () => {
    if (!propertyId) return;
    try {
      const { data: res } = await api.get(`/admin/users?role=resident&property_id=${propertyId}`);
      setResidents(res.items || []);
      const { data: l } = await api.get(`/admin/leases?property_id=${propertyId}`);
      setLeases(l.items || []);
    } catch (e) { if (typeof console !== 'undefined') console.debug('pages/admin/UnitTurnoverPage.jsx: swallowed', e); }
  };
  const loadTemplate = async () => {
    try {
      const { data } = await api.get("/admin/unit-inspections/room-template");
      setForm((f) => ({ ...f, rooms: data.rooms || [] }));
    } catch (e) { if (typeof console !== 'undefined') console.debug('pages/admin/UnitTurnoverPage.jsx: swallowed', e); }
  };

  useEffect(() => {
    load();
    loadPeople();
  }, [propertyId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const openDialog = () => {
    loadTemplate();
    setDialog(true);
  };

  const create = async () => {
    if (!form.unit) { toast.error("Unit is required"); return; }
    try {
      const { data } = await api.post("/admin/unit-inspections", {
        property_id: propertyId,
        ...form,
      });
      toast.success(`${form.type === "move_in" ? "Move-in" : "Move-out"} inspection created`);
      setDialog(false);
      setForm({ unit: "", resident_id: "", lease_id: "", type: "move_in", overall_condition: "good", rooms: [], damages: [], notes: "" });
      load();
      // Auto-open disposition modal for move-out
      if (data.type === "move_out") {
        setSelected(data);
        setDisp({ security_deposit_held: 0, deductions: [{ _uid: crypto.randomUUID(), reason: "", amount: 0 }] });
        setDispDialog(true);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
  };

  const openDispose = (item) => {
    setSelected(item);
    const src = item.deposit_disposition?.deductions?.length
      ? item.deposit_disposition.deductions
      : [{ reason: "", amount: 0 }];
    setDisp({
      security_deposit_held: item.deposit_disposition?.security_deposit_held || 0,
      deductions: src.map((d) => ({ _uid: d._uid || crypto.randomUUID(), ...d })),
    });
    setDispDialog(true);
  };

  const saveDisposition = async () => {
    if (!selected) return;
    try {
      const { data } = await api.post(`/admin/unit-inspections/${selected.id}/deposit-disposition`, {
        security_deposit_held: parseFloat(disp.security_deposit_held || 0),
        deductions: disp.deductions
          .filter((d) => d.reason && d.amount)
          .map((d) => ({ reason: d.reason, amount: parseFloat(d.amount) })),
      });
      toast.success(`Refund of $${data.deposit_disposition.refund_amount.toLocaleString()} computed`);
      setDispDialog(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const markRefundSent = async (item) => {
    const ref = window.prompt("Reference (check #, ACH id, etc.)?");
    if (ref === null) return;
    try {
      await api.post(`/admin/unit-inspections/${item.id}/deposit-refund-sent`, { refund_ref: ref });
      toast.success("Refund marked sent");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const addDamage = () => setForm({ ...form, damages: [...form.damages, { _uid: crypto.randomUUID(), location: "", description: "", estimated_cost: 0 }] });
  const updateDamage = (i, k, v) => {
    const copy = [...form.damages]; copy[i][k] = v; setForm({ ...form, damages: copy });
  };
  const removeDamage = (i) => setForm({ ...form, damages: form.damages.filter((_, idx) => idx !== i) });

  const addDeduction = () => setDisp({ ...disp, deductions: [...disp.deductions, { _uid: crypto.randomUUID(), reason: "", amount: 0 }] });

  return (
    <div className="space-y-6" data-testid="unit-turnover-page">
      <PageHeader
        eyebrow="Turnover"
        title="Move-In / Move-Out Inspections"
        subtitle="Document unit condition + compute security-deposit refunds under state law."
        actions={
          <Button onClick={openDialog} className="gap-2" data-testid="new-inspection-btn">
            <Plus size={14} /> New inspection
          </Button>
        }
      />

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="p-4 border-white/10 bg-card">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</div>
            <div className="text-2xl font-semibold mt-1" data-testid="stat-total">{stats.count}</div>
          </Card>
          <Card className="p-4 border-white/10 bg-card">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Move-ins</div>
            <div className="text-2xl font-semibold mt-1 text-emerald-500 flex items-center gap-2">
              <DoorOpen size={20} /> {stats.move_ins}
            </div>
          </Card>
          <Card className="p-4 border-white/10 bg-card">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Move-outs</div>
            <div className="text-2xl font-semibold mt-1 text-amber-500 flex items-center gap-2">
              <DoorClosed size={20} /> {stats.move_outs}
            </div>
          </Card>
          <Card className="p-4 border-white/10 bg-card">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Deposits Pending</div>
            <div className="text-2xl font-semibold mt-1 text-rose-400 flex items-center gap-2" data-testid="stat-deposits-pending">
              <DollarSign size={20} /> {stats.deposits_disposition_pending}
            </div>
          </Card>
        </div>
      )}

      <Card className="border-white/10 bg-card overflow-hidden">
        <div className="p-3 text-xs uppercase tracking-wider text-muted-foreground border-b border-white/10">Recent inspections</div>
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead>Unit</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Resident</TableHead>
              <TableHead>Performed</TableHead>
              <TableHead>Condition</TableHead>
              <TableHead>Damages</TableHead>
              <TableHead>Deposit</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow className="border-white/5"><TableCell colSpan={8} className="text-center py-8 text-muted-foreground text-sm" data-testid="inspections-empty">
                No inspections yet. Create the first move-in when a resident receives keys.
              </TableCell></TableRow>
            )}
            {items.map((it) => (
              <TableRow key={it.id} className="border-white/5" data-testid={`inspection-row-${it.id}`}>
                <TableCell className="font-medium">{it.unit}</TableCell>
                <TableCell>
                  <Badge variant={it.type === "move_in" ? "default" : "secondary"}>
                    {it.type === "move_in" ? "Move-in" : "Move-out"}
                  </Badge>
                </TableCell>
                <TableCell>{it.resident_name || "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{(it.performed_at || "").slice(0, 10)}</TableCell>
                <TableCell>
                  <Badge variant="outline">{it.overall_condition}</Badge>
                </TableCell>
                <TableCell className="text-sm">{(it.damages || []).length}</TableCell>
                <TableCell className="text-sm">
                  {it.type === "move_out" ? (
                    it.deposit_disposition?.refund_status === "sent" ? (
                      <Badge variant="default" className="bg-emerald-600">Refund sent</Badge>
                    ) : it.deposit_disposition?.refund_amount != null ? (
                      <span data-testid={`disp-refund-${it.id}`}>${it.deposit_disposition.refund_amount.toLocaleString()} pending</span>
                    ) : (
                      <span className="text-amber-500">Not computed</span>
                    )
                  ) : "—"}
                </TableCell>
                <TableCell className="text-right space-x-2">
                  {it.type === "move_out" && !it.deposit_disposition?.refund_amount && (
                    <Button size="sm" variant="outline" onClick={() => openDispose(it)} data-testid={`compute-disp-${it.id}`}>Compute disposition</Button>
                  )}
                  {it.type === "move_out" && it.deposit_disposition?.refund_amount != null && it.deposit_disposition?.refund_status !== "sent" && (
                    <Button size="sm" onClick={() => markRefundSent(it)} data-testid={`refund-sent-${it.id}`}>Mark refund sent</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Create inspection dialog */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="inspection-dialog">
          <DialogHeader><DialogTitle>New inspection</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="inspection-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="move_in">Move-in</SelectItem>
                  <SelectItem value="move_out">Move-out</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Unit</Label>
              <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="inspection-unit" />
            </div>
            <div className="space-y-2">
              <Label>Resident</Label>
              <Select value={form.resident_id} onValueChange={(v) => setForm({ ...form, resident_id: v })}>
                <SelectTrigger data-testid="inspection-resident"><SelectValue placeholder="Select resident" /></SelectTrigger>
                <SelectContent>
                  {residents.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} · {r.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Lease (optional)</Label>
              <Select value={form.lease_id} onValueChange={(v) => setForm({ ...form, lease_id: v })}>
                <SelectTrigger data-testid="inspection-lease"><SelectValue placeholder="Link to lease" /></SelectTrigger>
                <SelectContent>
                  {leases.map((l) => <SelectItem key={l.id} value={l.id}>{l.unit} · ${l.monthly_rent}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Overall condition</Label>
              <Select value={form.overall_condition} onValueChange={(v) => setForm({ ...form, overall_condition: v })}>
                <SelectTrigger data-testid="inspection-condition"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2">
              <Label className="mb-2 block">Rooms</Label>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {form.rooms.map((r, i) => (
                  <div key={r.name || `room-${i}`} className="grid grid-cols-6 gap-2 items-center text-sm">
                    <div className="col-span-2">{r.name}</div>
                    <Select value={r.condition} onValueChange={(v) => {
                      const copy = [...form.rooms]; copy[i].condition = v; setForm({ ...form, rooms: copy });
                    }}>
                      <SelectTrigger className="col-span-2"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input className="col-span-2" placeholder="Notes" value={r.notes || ""} onChange={(e) => {
                      const copy = [...form.rooms]; copy[i].notes = e.target.value; setForm({ ...form, rooms: copy });
                    }} />
                  </div>
                ))}
              </div>
            </div>

            <div className="col-span-2">
              <div className="flex items-center justify-between mb-2">
                <Label>Damages</Label>
                <Button size="sm" variant="outline" onClick={addDamage} data-testid="add-damage-btn">+ Add damage</Button>
              </div>
              <div className="space-y-2">
                {form.damages.map((d, i) => (
                  <div key={d._uid || `dmg-${i}`} className="grid grid-cols-8 gap-2 items-center">
                    <Input className="col-span-2" placeholder="Location" value={d.location} onChange={(e) => updateDamage(i, "location", e.target.value)} />
                    <Input className="col-span-4" placeholder="Description" value={d.description} onChange={(e) => updateDamage(i, "description", e.target.value)} />
                    <Input className="col-span-1" type="number" placeholder="Cost" value={d.estimated_cost} onChange={(e) => updateDamage(i, "estimated_cost", parseFloat(e.target.value || 0))} />
                    <Button size="icon" variant="ghost" onClick={() => removeDamage(i)}><X size={12} /></Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2 col-span-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="inspection-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button onClick={create} data-testid="inspection-save">Save inspection</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deposit disposition modal */}
      <Dialog open={dispDialog} onOpenChange={setDispDialog}>
        <DialogContent className="max-w-lg" data-testid="disposition-dialog">
          <DialogHeader>
            <DialogTitle>Security Deposit Disposition · Unit {selected?.unit}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Security deposit held ($)</Label>
              <Input type="number" value={disp.security_deposit_held}
                onChange={(e) => setDisp({ ...disp, security_deposit_held: e.target.value })}
                data-testid="disp-held" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Deductions</Label>
                <Button size="sm" variant="outline" onClick={addDeduction} data-testid="add-deduction">+ Add</Button>
              </div>
              <div className="space-y-2">
                {disp.deductions.map((d, i) => (
                  <div key={d._uid || `ded-${i}`} className="grid grid-cols-3 gap-2">
                    <Input className="col-span-2" placeholder="Reason (e.g. carpet cleaning)"
                      value={d.reason}
                      onChange={(e) => {
                        const copy = [...disp.deductions]; copy[i].reason = e.target.value; setDisp({ ...disp, deductions: copy });
                      }}
                      data-testid={`disp-reason-${i}`}
                    />
                    <Input type="number" placeholder="Amount" value={d.amount}
                      onChange={(e) => {
                        const copy = [...disp.deductions]; copy[i].amount = e.target.value; setDisp({ ...disp, deductions: copy });
                      }}
                      data-testid={`disp-amount-${i}`}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="text-lg pt-3 border-t border-white/10">
              Refund: <strong data-testid="disp-refund-preview">${
                Math.max(
                  parseFloat(disp.security_deposit_held || 0) -
                  disp.deductions.reduce((s, d) => s + parseFloat(d.amount || 0), 0),
                  0
                ).toLocaleString()
              }</strong>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDispDialog(false)}>Cancel</Button>
            <Button onClick={saveDisposition} data-testid="disp-save">Compute + save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
